import { useEffect, useState } from 'react'
import { dataManagerApi } from '../../../services/dataManagerApi'
import type { ParsedFile } from './Step2_UploadFile'

interface PreviewResult {
  valid: boolean
  mapped_rows: Record<string, unknown>[]
  total_rows: number
  valid_count: number
  warning_count: number
  error_count: number
  error_details: Array<{ row: number; field: string; error: string }>
}

interface ImportResult {
  target: string
  imported_count: number
  skipped_count: number
  error_count: number
  error_rows: Array<{ row: number; error: string }>
  total_rows: number
}

interface Step4Props {
  companyId: number
  target: string
  parsedFile: ParsedFile
  columnMappings: Record<string, string>
  onComplete: () => void
  onBack: () => void
}

function Step4_ValidateConfirm({
  companyId,
  target,
  parsedFile,
  columnMappings,
  onComplete,
  onBack,
}: Step4Props) {
  const [previewResult, setPreviewResult] = useState<PreviewResult | null>(null)
  const [importResult, setImportResult] = useState<ImportResult | null>(null)
  const [loading, setLoading] = useState(false)
  const [importing, setImporting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [onDuplicate, setOnDuplicate] = useState('skip')
  const [showErrors, setShowErrors] = useState(false)
  const [showImportErrors, setShowImportErrors] = useState(false)

  // Run preview validation on mount
  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setError(null)

    dataManagerApi
      .previewImport(companyId, target, columnMappings)
      .then((result) => {
        if (!cancelled) setPreviewResult(result)
      })
      .catch((e: unknown) => {
        if (!cancelled) setError(e instanceof Error ? e.message : 'Validation failed')
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })

    return () => { cancelled = true }
  }, [companyId, target, columnMappings])

  const handleImport = async () => {
    setImporting(true)
    setError(null)
    try {
      const result = await dataManagerApi.executeImport(
        companyId,
        target,
        columnMappings,
        onDuplicate
      )
      setImportResult(result)
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Import failed')
    } finally {
      setImporting(false)
    }
  }

  // Loading state
  if (loading) {
    return (
      <div>
        <div className="iw-step-title">Validating</div>
        <div className="iw-loading">
          <span className="iw-spinner" />
          Validating {parsedFile.row_count} rows...
        </div>
      </div>
    )
  }

  // Error state
  if (error && !previewResult && !importResult) {
    return (
      <div>
        <div className="iw-step-title">Validation Error</div>
        <div className="iw-error">{error}</div>
        <div className="iw-btn-row">
          <button className="iw-btn iw-btn--secondary" onClick={onBack}>
            Back
          </button>
        </div>
      </div>
    )
  }

  // Import complete — show results
  if (importResult) {
    return (
      <div>
        <div className="iw-step-title">Import Complete</div>
        <div className="iw-step-subtitle">Your data has been processed.</div>

        <div className="iw-result-summary">
          <div className="iw-result-row">
            <span className="iw-result-label">Imported</span>
            <span className="iw-result-value iw-result-value--success">
              {importResult.imported_count}
            </span>
          </div>
          <div className="iw-result-row">
            <span className="iw-result-label">Skipped</span>
            <span className="iw-result-value iw-result-value--skip">
              {importResult.skipped_count}
            </span>
          </div>
          <div className="iw-result-row">
            <span className="iw-result-label">Errors</span>
            <span className={`iw-result-value${importResult.error_count > 0 ? ' iw-result-value--error' : ''}`}>
              {importResult.error_count}
            </span>
          </div>
          <div className="iw-result-row">
            <span className="iw-result-label">Total rows</span>
            <span className="iw-result-value">{importResult.total_rows}</span>
          </div>
        </div>

        {importResult.error_rows.length > 0 && (
          <div>
            <button
              className="iw-error-toggle"
              onClick={() => setShowImportErrors((p) => !p)}
            >
              {showImportErrors ? 'Hide' : 'Show'} error details ({importResult.error_rows.length})
            </button>
            {showImportErrors && (
              <div className="iw-error-list">
                {importResult.error_rows.map((e, i) => (
                  <div key={i} className="iw-error-item">
                    Row {e.row}: {e.error}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        <div className="iw-btn-row">
          <button className="iw-btn iw-btn--primary" onClick={onComplete}>
            Import More
          </button>
        </div>
      </div>
    )
  }

  // Preview / validation results
  if (!previewResult) return null

  const validCount = previewResult.valid_count
  const errorCount = previewResult.error_count
  const totalRows = previewResult.total_rows
  const previewRows = previewResult.mapped_rows?.slice(0, 3) ?? []
  const previewKeys = previewRows.length > 0 ? Object.keys(previewRows[0]) : []

  return (
    <div>
      <div className="iw-step-title">Review & Import</div>
      <div className="iw-step-subtitle">Validate your data before importing.</div>

      {error && (
        <div className="iw-error">
          {error}
          <button className="iw-error-dismiss" onClick={() => setError(null)}>x</button>
        </div>
      )}

      {/* Validation summary */}
      <div className="iw-validation-summary">
        <div className="iw-validation-stat iw-validation-stat--total">
          <span className="iw-validation-stat-value">{totalRows}</span>
          <span className="iw-validation-stat-label">Total Rows</span>
        </div>
        <div className="iw-validation-stat iw-validation-stat--valid">
          <span className="iw-validation-stat-value">{validCount}</span>
          <span className="iw-validation-stat-label">Valid</span>
        </div>
        {errorCount > 0 && (
          <div className="iw-validation-stat iw-validation-stat--error">
            <span className="iw-validation-stat-value">{errorCount}</span>
            <span className="iw-validation-stat-label">Errors</span>
          </div>
        )}
      </div>

      {/* Error details */}
      {previewResult.error_details.length > 0 && (
        <div>
          <button
            className="iw-error-toggle"
            onClick={() => setShowErrors((p) => !p)}
          >
            {showErrors ? 'Hide' : 'Show'} error details ({previewResult.error_details.length})
          </button>
          {showErrors && (
            <div className="iw-error-list">
              {previewResult.error_details.map((e, i) => (
                <div key={i} className="iw-error-item">
                  Row {e.row} ({e.field}): {e.error}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Preview of mapped rows */}
      {previewRows.length > 0 && (
        <div className="iw-preview-section">
          <div className="iw-preview-label">Mapped data preview (first {previewRows.length} rows)</div>
          <div className="iw-preview-table-wrap">
            <table className="iw-preview-table">
              <thead>
                <tr>
                  {previewKeys.map((k) => (
                    <th key={k}>{k}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {previewRows.map((row, i) => (
                  <tr key={i}>
                    {previewKeys.map((k) => (
                      <td key={k}>{row[k] != null ? String(row[k]) : ''}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Duplicate handling */}
      <div className="iw-dup-row">
        <span className="iw-dup-label">On duplicate:</span>
        <select
          className="iw-dup-select"
          value={onDuplicate}
          onChange={(e) => setOnDuplicate(e.target.value)}
        >
          <option value="skip">Skip</option>
          <option value="update">Update</option>
          <option value="error">Error</option>
        </select>
      </div>

      {/* Action buttons */}
      <div className="iw-btn-row">
        <button className="iw-btn iw-btn--secondary" onClick={onBack}>
          Back
        </button>
        <button
          className="iw-btn iw-btn--primary"
          disabled={importing || validCount === 0}
          onClick={handleImport}
        >
          {importing ? (
            <>
              <span className="iw-spinner" />
              Importing...
            </>
          ) : (
            `Import ${validCount} rows`
          )}
        </button>
      </div>
    </div>
  )
}

export default Step4_ValidateConfirm
